import React from "react";
import {
  PropertyDetails,
  PropertyLvrBand,
  PropertyFeeCharge,
  propertyPrimaryPurposeOptions,
  propertyRepaymentTypeOptions,
  propertyValuationOptions,
  propertyFundingSpeedOptions,
  propertyIncomeVerificationOptions,
  propertyAtoDebtOptions,
  propertyCreditProfileOptions,
  propertySecurityPositionOptions,
  formatCurrencyWithCommasInput,
  formatIntegerInput,
  formatTwoDecimalInput,
  finalizeTwoDecimalInput,
  toggleOptionInArray,
} from "./loanProductUtils";

type Props = {
  details: PropertyDetails;
  onChange: (next: PropertyDetails) => void;
};

/* ─── helpers ─── */
/** Strip $ and commas to get a plain number for comparison */
const parseCurrency = (v: string) => parseFloat(v.replace(/[$,]/g, "")) || 0;
const parseNum = (v: string) => parseFloat(v) || 0;

/* ─── Inline checkbox row: label on left, checkboxes flowing right ─── */
const InlineCheckboxRow = ({
  label,
  options,
  selected,
  onToggle,
}: {
  label: string;
  options: string[];
  selected: string[];
  onToggle: (value: string, checked: boolean) => void;
}) => (
  <div style={{ display: "flex", alignItems: "flex-start", marginBottom: "10px" }}>
    <div style={{ minWidth: "180px", fontWeight: 500, paddingTop: "2px", fontSize: "14px" }}>
      {label}
    </div>
    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 20px" }}>
      {options.map((option) => (
        <div key={option} className="form-check" style={{ margin: 0, minWidth: "60px" }}>
          <input
            className="form-check-input"
            type="checkbox"
            id={`${label}-${option}`}
            checked={selected.includes(option)}
            onChange={(e) => onToggle(option, e.target.checked)}
          />
          <label className="form-check-label" htmlFor={`${label}-${option}`} style={{ fontSize: "13px" }}>
            {option}
          </label>
        </div>
      ))}
    </div>
  </div>
);

/* ─── Inline Yes/No row: label left, styled select right ─── */
const InlineYesNoRow = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value: "" | "Yes" | "No";
  onChange: (value: "" | "Yes" | "No") => void;
}) => (
  <div style={{ display: "flex", alignItems: "center", marginBottom: "10px" }}>
    <div style={{ minWidth: "180px", fontWeight: 500, fontSize: "14px" }}>{label}</div>
    <select
      className="form-select"
      style={{ width: "110px", fontSize: "13px", padding: "3px 6px", height: "32px" }}
      value={value}
      onChange={(e) => onChange(e.target.value as "" | "Yes" | "No")}
    >
      <option value="Yes">Yes</option>
      <option value="No">No</option>
    </select>
  </div>
);

const PropertyProductTypeFields = ({ details, onChange }: Props) => {
  const lvrRateColumns: Array<{
    label: string;
    fromField: keyof PropertyLvrBand;
    toField: keyof PropertyLvrBand;
  }> = [
      { label: "Residential 1st Mortgage", fromField: "residential1stFrom", toField: "residential1stTo" },
      { label: "Residential 2nd Mortgage", fromField: "residential2ndFrom", toField: "residential2ndTo" },
      { label: "Commercial", fromField: "commercialFrom", toField: "commercialTo" },
      { label: "Farm/Rural", fromField: "farmRuralFrom", toField: "farmRuralTo" },
    ];

  const updateBand = (index: number, patch: Partial<PropertyLvrBand>) => {
    const next = details.lvrInterestRates.map((band, i) =>
      i !== index ? band : { ...band, ...patch }
    );
    onChange({ ...details, lvrInterestRates: next });
  };

  const updateBandField = (index: number, field: keyof PropertyLvrBand, value: string) => {
    const patch: Partial<PropertyLvrBand> = { [field]: value };
    if (field.endsWith('From')) {
      const toField = field.replace('From', 'To') as keyof PropertyLvrBand;
      const numValue = parseFloat(value);
      if (!isNaN(numValue)) {
        patch[toField] = (numValue + 1).toFixed(2);
      } else if (value === "") {
        patch[toField] = "";
      }
    }
    updateBand(index, patch);
  };

  const updateFee = (index: number, patch: Partial<PropertyFeeCharge>) => {
    const next = details.feesAndCharges.map((fee, i) =>
      i !== index ? fee : { ...fee, ...patch }
    );
    onChange({ ...details, feesAndCharges: next });
  };

  /* ── validation helpers ── */
  const amountMaxInvalid =
    details.loanAmountMin !== "" &&
    details.loanAmountMax !== "" &&
    parseCurrency(details.loanAmountMax) <= parseCurrency(details.loanAmountMin);

  const termMaxInvalid =
    details.loanTermMonthsMin !== "" &&
    details.loanTermMonthsMax !== "" &&
    parseNum(details.loanTermMonthsMax) <= parseNum(details.loanTermMonthsMin);

  /* ── shared input styles ── */
  /** Currency input — wide enough for $99,999,999 */
  const inputCurrency: React.CSSProperties = {
    width: "10ch",
    fontSize: "13px",
    padding: "3px 6px",
    height: "32px",
    display: "inline-block",
  };

  /** Short integer input — 4 chars (e.g. 999) */
  const inputInt: React.CSSProperties = {
    width: "6ch",
    fontSize: "13px",
    padding: "3px 6px",
    height: "32px",
    display: "inline-block",
  };

  /** Percentage input — just enough for 99.99 */
  const inputPct: React.CSSProperties = {
    width: "7ch",
    fontSize: "13px",
    padding: "3px 6px",
    height: "32px",
    display: "inline-block",
  };

  const labelStyle: React.CSSProperties = { minWidth: "180px", fontWeight: 500, fontSize: "14px" };
  const rowStyle: React.CSSProperties = { display: "flex", alignItems: "center", marginBottom: "10px", flexWrap: "wrap", gap: "6px" };
  const inlineLabel: React.CSSProperties = { fontSize: "13px", color: "#444" };

  const errorBorder: React.CSSProperties = { borderColor: "#dc3545", outline: "none", boxShadow: "0 0 0 .15rem rgba(220,53,69,.25)" };

  return (
    <div className="col-12 border rounded p-3 mt-2">

      {/* ══════════════ LOAN DETAILS ══════════════ */}
      <h5 className="mb-3 text-primary fw-bold" style={{ borderBottom: '2px solid #0d6efd', paddingBottom: '5px' }}>Loan Details</h5>

      {/* Loan Amount */}
      <div style={rowStyle}>
        <span style={labelStyle}>Loan Amount</span>
        <span style={inlineLabel}>Minimum</span>
        <input
          className="form-control loan-compact-input"
          style={inputCurrency}
          value={details.loanAmountMin}
          onChange={(e) => onChange({ ...details, loanAmountMin: formatCurrencyWithCommasInput(e.target.value) })}
          placeholder="$99,999,999"
        />
        <span style={{ ...inlineLabel, marginLeft: "16px" }}>Maximum</span>
        <input
          className="form-control loan-compact-input"
          style={{ ...inputCurrency, ...(amountMaxInvalid ? errorBorder : {}) }}
          value={details.loanAmountMax}
          onChange={(e) => onChange({ ...details, loanAmountMax: formatCurrencyWithCommasInput(e.target.value) })}
          placeholder="$99,999,999"
          title={amountMaxInvalid ? "Maximum must be greater than Minimum" : undefined}
        />
        {amountMaxInvalid && (
          <span style={{ fontSize: "12px", color: "#dc3545" }}>Max must be &gt; Min</span>
        )}
      </div>

      {/* Loan Term Months */}
      <div style={rowStyle}>
        <span style={labelStyle}>Loan Term Months</span>
        <span style={inlineLabel}>Minimum</span>
        <input
          className="form-control loan-compact-input"
          style={inputInt}
          value={details.loanTermMonthsMin}
          onChange={(e) => onChange({ ...details, loanTermMonthsMin: formatIntegerInput(e.target.value, 3) })}
          placeholder="99"
        />
        <span style={{ ...inlineLabel, marginLeft: "16px" }}>Maximum</span>
        <input
          className="form-control loan-compact-input"
          style={{ ...inputInt, ...(termMaxInvalid ? errorBorder : {}) }}
          value={details.loanTermMonthsMax}
          onChange={(e) => onChange({ ...details, loanTermMonthsMax: formatIntegerInput(e.target.value, 3) })}
          placeholder="999"
          title={termMaxInvalid ? "Maximum must be greater than Minimum" : undefined}
        />
        {termMaxInvalid && (
          <span style={{ fontSize: "12px", color: "#dc3545" }}>Max must be &gt; Min</span>
        )}
      </div>

      {/* Primary Purpose */}
      <InlineCheckboxRow
        label="Primary Purpose"
        options={propertyPrimaryPurposeOptions}
        selected={details.primaryPurpose}
        onToggle={(value, checked) =>
          onChange({ ...details, primaryPurpose: toggleOptionInArray(details.primaryPurpose, value, checked) })
        }
      />

      {/* Interest Type */}
      <InlineCheckboxRow
        label="Interest Type"
        options={propertyRepaymentTypeOptions}
        selected={details.repaymentType}
        onToggle={(value, checked) =>
          onChange({ ...details, repaymentType: toggleOptionInArray(details.repaymentType, value, checked) })
        }
      />







      {/* Maximum Extension Allowed + Maximum Months */}
      {/* <div style={rowStyle}>
        <span style={labelStyle}>Maximum Extension Allowed</span>
        <input
          className="form-control loan-compact-input"
          style={inputInt}
          value={details.maximumExtensionAllowed}
          onChange={(e) => onChange({ ...details, maximumExtensionAllowed: formatIntegerInput(e.target.value, 3) })}
          placeholder="0"
        />
        <span style={{ ...inlineLabel, marginLeft: "16px" }}>Maximum Months</span>
        <input
          className="form-control loan-compact-input"
          style={inputInt}
          value={details.maximumExtensionMonths}
          onChange={(e) => onChange({ ...details, maximumExtensionMonths: formatIntegerInput(e.target.value, 3) })}
          placeholder="999"
        />
      </div> */}

      {/* Valuation */}
      <InlineCheckboxRow
        label="Valuation"
        options={propertyValuationOptions}
        selected={details.valuation}
        onToggle={(value, checked) =>
          onChange({ ...details, valuation: toggleOptionInArray(details.valuation, value, checked) })
        }
      />





      {/* ══════════════ BORROWER PROFILE ══════════════ */}
      <h5 className="mt-4 mb-3 text-primary fw-bold" style={{ borderBottom: '2px solid #0d6efd', paddingBottom: '5px' }}>Income Verification</h5>

      <InlineCheckboxRow
        label="Income Verification"
        options={propertyIncomeVerificationOptions}
        selected={details.incomeVerification}
        onToggle={(value, checked) =>
          onChange({ ...details, incomeVerification: toggleOptionInArray(details.incomeVerification, value, checked) })
        }
      />

      <InlineCheckboxRow
        label="ATO Debt"
        options={propertyAtoDebtOptions}
        selected={details.atoDebt}
        onToggle={(value, checked) =>
          onChange({ ...details, atoDebt: toggleOptionInArray(details.atoDebt, value, checked) })
        }
      />

      <InlineCheckboxRow
        label="Credit Profile"
        options={propertyCreditProfileOptions}
        selected={details.creditProfile}
        onToggle={(value, checked) =>
          onChange({ ...details, creditProfile: toggleOptionInArray(details.creditProfile, value, checked) })
        }
      />



      {/* ══════════════ SECURITY PROPERTY ══════════════ */}
      <h5 className="mt-4 mb-3 text-primary fw-bold" style={{ borderBottom: '2px solid #0d6efd', paddingBottom: '5px' }}>Security Property</h5>

      <InlineCheckboxRow
        label="Security Position"
        options={propertySecurityPositionOptions}
        selected={details.securityPosition}
        onToggle={(value, checked) =>
          onChange({ ...details, securityPosition: toggleOptionInArray(details.securityPosition, value, checked) })
        }
      />

      <div style={{ display: "flex", alignItems: "flex-start", marginBottom: "10px" }}>
        <div style={{ minWidth: "180px", fontWeight: 500, paddingTop: "2px", fontSize: "14px" }}>
          Property Type - Residential
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 20px" }}>
          {["Yes", "No"].map((option) => (
            <div key={option} className="form-check" style={{ margin: 0, minWidth: "60px" }}>
              <input
                className="form-check-input"
                type="radio"
                name="propertyTypeResidential"
                id={`PropertyTypeResidential-${option}`}
                checked={details.propertyTypeResidential === option}
                onChange={() => onChange({ ...details, propertyTypeResidential: option as "Yes" | "No" })}
              />
              <label className="form-check-label" htmlFor={`PropertyTypeResidential-${option}`} style={{ fontSize: "13px" }}>
                {option}
              </label>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "flex-start", marginBottom: "10px" }}>
        <div style={{ minWidth: "180px", fontWeight: 500, paddingTop: "2px", fontSize: "14px" }}>
          Property Type - Commercial
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 20px" }}>
          {["Yes", "No"].map((option) => (
            <div key={option} className="form-check" style={{ margin: 0, minWidth: "60px" }}>
              <input
                className="form-check-input"
                type="radio"
                name="propertyTypeCommercial"
                id={`PropertyTypeCommercial-${option}`}
                checked={details.propertyTypeCommercial === option}
                onChange={() => onChange({ ...details, propertyTypeCommercial: option as "Yes" | "No" })}
              />
              <label className="form-check-label" htmlFor={`PropertyTypeCommercial-${option}`} style={{ fontSize: "13px" }}>
                {option}
              </label>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "flex-start", marginBottom: "10px" }}>
        <div style={{ minWidth: "180px", fontWeight: 500, paddingTop: "2px", fontSize: "14px" }}>
          Property Type - Rural
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 20px" }}>
          {["Yes", "No"].map((option) => (
            <div key={option} className="form-check" style={{ margin: 0, minWidth: "60px" }}>
              <input
                className="form-check-input"
                type="radio"
                name="propertyTypeRural"
                id={`PropertyTypeRural-${option}`}
                checked={details.propertyTypeRural === option}
                onChange={() => onChange({ ...details, propertyTypeRural: option as "Yes" | "No" })}
              />
              <label className="form-check-label" htmlFor={`PropertyTypeRural-${option}`} style={{ fontSize: "13px" }}>
                {option}
              </label>
            </div>
          ))}
        </div>
      </div>

      <InlineCheckboxRow
        label="States Accepted"
        options={["ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA"]}
        selected={details.statesAccepted || []}
        onToggle={(value, checked) =>
          onChange({ ...details, statesAccepted: toggleOptionInArray(details.statesAccepted || [], value, checked) })
        }
      />

      {/* ══════════════ LVR & INTEREST RATES ══════════════ */}
      <h5 className="mt-4 mb-3 text-primary fw-bold" style={{ borderBottom: '2px solid #0d6efd', paddingBottom: '5px' }}>
        Loan to Valuation Ration (LVR) &amp; Interest Rates
      </h5>

      <div style={{ overflowX: "auto" }}>
        <table style={{ borderCollapse: "collapse", width: "100%", minWidth: "800px" }}>
          {/* ── Header ── */}
          <thead>
            <tr>
              <th
                style={{
                  fontWeight: 700,
                  color: "#0d6efd",
                  fontSize: "14px",
                  padding: "4px 6px 6px 0",
                  whiteSpace: "nowrap",
                  width: "80px",
                  borderBottom: "1px solid #dee2e6",
                }}
              >
                LVR
              </th>
              {lvrRateColumns.map((col) => (
                <th
                  key={col.label}
                  style={{
                    fontWeight: 700,
                    color: "#0d6efd",
                    fontSize: "13px",
                    padding: "4px 8px 6px 8px",
                    textAlign: "left",
                    whiteSpace: "nowrap",
                    borderBottom: "1px solid #dee2e6",
                  }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>

          {/* ── Rows ── */}
          <tbody>
            {details.lvrInterestRates.map((band, index) => (
              <tr key={band.lvrLabel}>
                {/* LVR label */}
                <td
                  style={{
                    fontSize: "13px",
                    fontWeight: 500,
                    padding: "7px 8px 7px 0",
                    borderBottom: "1px solid #dee2e6",
                    whiteSpace: "nowrap",
                  }}
                >
                  {band.lvrLabel}
                </td>

                {/* Each mortgage column */}
                {lvrRateColumns.map((col) => (
                  <td
                    key={col.label}
                    style={{
                      padding: "6px 8px",
                      borderBottom: "1px solid #dee2e6",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: "5px", flexWrap: "nowrap" }}>
                      <span style={{ fontSize: "12px", color: "#555" }}>From</span>
                      <input
                        className="form-control loan-compact-input"
                        style={inputPct}
                        value={band[col.fromField] as string}
                        onChange={(e) => updateBandField(index, col.fromField, formatTwoDecimalInput(e.target.value, 2))}
                        onBlur={(e) => updateBandField(index, col.fromField, finalizeTwoDecimalInput(e.target.value, 2))}
                        placeholder="99.99"
                      />
                      <span style={{ fontSize: "12px", color: "#555" }}>To</span>
                      <input
                        className="form-control loan-compact-input"
                        style={inputPct}
                        value={band[col.toField] as string}
                        onChange={(e) => updateBandField(index, col.toField, formatTwoDecimalInput(e.target.value, 2))}
                        onBlur={(e) => updateBandField(index, col.toField, finalizeTwoDecimalInput(e.target.value, 2))}
                        placeholder="99.99"
                      />
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>



      <div style={{ marginTop: "30px", borderBottom: '2px solid #0d6efd', paddingBottom: '10px', marginBottom: "20px" }}>
        <h5 className="text-primary fw-bold m-0">Fees &amp; Charges</h5>
      </div>

      <div>
        {/* ══════════════ FEES & CHARGES ══════════════ */}
        <div>
          {details.feesAndCharges.map((fee, index) => (
            <div
              key={fee.label}
              style={{ display: "flex", alignItems: "center", marginBottom: "8px", gap: "8px", flexWrap: "wrap" }}
            >
              <div style={{ minWidth: "160px", fontSize: "14px", fontWeight: 500 }}>{fee.label}</div>
              <input
                className="form-control loan-compact-input"
                style={{ ...inputPct, textAlign: "center", border: "1px solid #000" }}
                value={fee.amount}
                onChange={(e) => updateFee(index, { amount: formatCurrencyWithCommasInput(e.target.value) })}
                placeholder="$999,999"
              />
              <span style={{ fontSize: "13px", fontWeight: 600 }}>OR</span>
              <input
                className="form-control loan-compact-input"
                style={{ ...inputPct, textAlign: "center", border: "1px solid #000" }}
                value={fee.percent}
                onChange={(e) => updateFee(index, { percent: formatTwoDecimalInput(e.target.value, 2) })}
                onBlur={(e) => updateFee(index, { percent: finalizeTwoDecimalInput(e.target.value, 2) })}
                placeholder="99.99"
              />
              <span style={{ fontSize: "13px", color: "#555" }}>of loan amount</span>
            </div>
          ))}
        </div>

        {/* ══════════════ FINTRACK BROKERAGE FEE ══════════════ */}
        <div>
          <h6 className="fw-bold mb-3 text-primary" style={{ fontSize: "14px" }}>Fintrack Brokerage Fee</h6>
          <div style={{ display: "flex", alignItems: "center", gap: "12px", marginTop: "0px" }}>
            <span style={{ fontSize: "14px", fontWeight: 500, minWidth: "155px" }}>Commission</span>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <input
                className="form-control loan-compact-input"
                style={{ ...inputPct, textAlign: "center", border: "1px solid #000" }}
                value={details.commissionFee}
                onChange={(e) => onChange({ ...details, commissionFee: formatTwoDecimalInput(e.target.value, 2) })}
                onBlur={(e) => onChange({ ...details, commissionFee: finalizeTwoDecimalInput(e.target.value, 2) })}
                placeholder="99.99%"
              />
              <span style={{ fontSize: "13px", color: "#555" }}>of loan amount</span>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default PropertyProductTypeFields;
